import { useMemo } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  categoryColor,
  formatCategoryName,
  formatCurrency,
  formatPaymentMonth,
  isUserVisibleCategory,
} from "./dashboardUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function hasAnyAmount(trends, category) {
  return trends.some(
    (month) => Number(month.categories?.[category] || 0) > 0
  );
}

export default function MonthlyTrendChart({
  trends = [],
  categoryTotals = [],
}) {
  const labels = trends.map((month) => formatPaymentMonth(month.paymentMonth));
  const categories = useMemo(() => {
    const ordered = [];
    const seen = new Set();

    categoryTotals.forEach((item) => {
      if (!isUserVisibleCategory(item.category) || seen.has(item.category)) {
        return;
      }

      const totalAmount = Number(item.totalAmount || 0);
      if (item.mandatory || totalAmount > 0 || hasAnyAmount(trends, item.category)) {
        ordered.push(item.category);
        seen.add(item.category);
      }
    });

    trends.forEach((month) => {
      Object.keys(month.categories || {}).forEach((category) => {
        if (
          isUserVisibleCategory(category) &&
          !seen.has(category) &&
          hasAnyAmount(trends, category)
        ) {
          ordered.push(category);
          seen.add(category);
        }
      });
    });

    return ordered;
  }, [categoryTotals, trends]);

  const data = {
    labels,
    datasets: categories.map((category, index) => ({
      label: formatCategoryName(category),
      data: trends.map((month) => Number(month.categories?.[category] || 0)),
      backgroundColor: categoryColor(category, index),
      borderRadius: 7,
      maxBarThickness: 42,
      stack: "payments",
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, usePointStyle: true },
      },
      tooltip: {
        callbacks: {
          label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { maxRotation: 0, autoSkip: true },
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  return (
    <section className="rounded-2xl bg-white p-4 shadow sm:p-5">
      <div>
        <h3 className="text-lg font-semibold text-zinc-950">
          Monthly Payment Trend
        </h3>
        <p className="text-sm text-zinc-500">
          Successful payments by user-visible category and reporting month.
        </p>
      </div>
      <div className="mt-4 h-80">
        {categories.length ? (
          <Bar data={data} options={options} />
        ) : (
          <div className="flex h-full items-center justify-center rounded-xl bg-zinc-50 text-sm text-zinc-500">
            No category data found for this year.
          </div>
        )}
      </div>
    </section>
  );
}
